import * as React from 'react';
import {
  pushOverlayBack,
  releaseOverlayBack,
  startAntEphemeralOverlayBackWatch,
} from 'utils/overlay-back-stack';
import { usePwaMobileNavEnabled } from 'utils/usePwaMobileNavEnabled';

/**
 * While `open`, register this overlay on the mobile back stack.
 * Back closes via `onClose`; programmatic close releases the history entry.
 */
export function useOverlayBackHandler(
  open: boolean,
  onClose: () => void,
  id: string,
  enabled = true
): void {
  const onCloseRef = React.useRef(onClose);
  onCloseRef.current = onClose;
  const registeredRef = React.useRef(false);

  React.useEffect(() => {
    if (!enabled || !open) {
      if (registeredRef.current) {
        registeredRef.current = false;
        releaseOverlayBack(id);
      }
      return;
    }

    registeredRef.current = true;
    pushOverlayBack(id, () => {
      onCloseRef.current();
    });

    return () => {
      if (registeredRef.current) {
        registeredRef.current = false;
        releaseOverlayBack(id);
      }
    };
  }, [enabled, open, id]);
}

/**
 * Mount once under the PWA mobile shell: Ant menus/dropdowns intercept Back.
 */
export function PwaOverlayBackProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const pwaMobileNav = usePwaMobileNavEnabled();

  React.useEffect(() => {
    if (!pwaMobileNav) {
      return undefined;
    }
    return startAntEphemeralOverlayBackWatch();
  }, [pwaMobileNav]);

  return <>{children}</>;
}
